import { useState, useEffect, useMemo, useRef } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
} from '@/components/v4';
import {
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    VStack,
    Box,
    Text,
    IconButton,
    HStack,
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon } from '@chakra-ui/icons';
import { useSelector } from 'react-redux';
import { axios } from '@/services/axios';
import { getPickerColors, getModalColors } from './pickerColors';

// Module-level cache - persists between modal opens
let cachedWholesalers = null;
let fetchPromise = null;

/**
 * Pre-fetch wholesalers data (call on app init or hover)
 */
export const prefetchWholesalers = async () => {
    if (cachedWholesalers) return cachedWholesalers;
    if (fetchPromise) return fetchPromise;

    fetchPromise = axios
        .get('/wholesalers/')
        .then((res) => {
            const list = res.data || [];
            // Pre-sort alphabetically
            list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            cachedWholesalers = list;
            fetchPromise = null;
            return list;
        })
        .catch((err) => {
            fetchPromise = null;
            throw err;
        });

    return fetchPromise;
};

/**
 * Reusable Wholesaler Picker Modal
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when selection is made. Receives { wholesalerId, wholesalerName }
 * @param {string} currentWholesalerId - Currently selected wholesaler ID (for highlighting)
 * @param {string} title - Modal title (default: "Select Wholesaler")
 */
const WholesalerPicker = ({
    isOpen,
    onClose,
    onSelect,
    currentWholesalerId = null,
    title = 'Select Wholesaler',
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    // Initialize from cache if available - instant!
    const [wholesalers, setWholesalers] = useState(
        () => cachedWholesalers || [],
    );
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(() => !!cachedWholesalers);
    const searchInputRef = useRef(null);

    const { darkMode } = useSelector((state) => state.settings);

    const fetchData = async () => {
        if (cachedWholesalers) {
            setWholesalers(cachedWholesalers);
            setDataLoaded(true);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const list = await prefetchWholesalers();
            setWholesalers(list);
            setDataLoaded(true);
        } catch (err) {
            console.error('[WholesalerPicker] Failed to load data:', err);
            setError('Failed to load wholesalers');
        } finally {
            setIsLoading(false);
        }
    };

    // Load data on mount
    useEffect(() => {
        if (!dataLoaded) {
            fetchData();
        }
    }, [dataLoaded]);

    // When modal opens, focus search input
    useEffect(() => {
        if (isOpen) {
            // Auto-focus search input immediately
            requestAnimationFrame(() => {
                searchInputRef.current?.focus();
            });
        }
    }, [isOpen]);

    // Filter wholesalers - instant, no debounce needed for small lists
    const filteredWholesalers = useMemo(() => {
        if (!searchTerm) {
            // No search - just move selected to top
            if (currentWholesalerId) {
                const selected = wholesalers.find(
                    (w) => w.id === currentWholesalerId,
                );
                if (selected) {
                    return [
                        selected,
                        ...wholesalers.filter(
                            (w) => w.id !== currentWholesalerId,
                        ),
                    ];
                }
            }
            return wholesalers;
        }

        // Apply search filter
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = wholesalers.filter(
            (wholesaler) =>
                wholesaler.name?.toLowerCase().includes(lowerSearch) ||
                wholesaler.id?.toLowerCase().includes(lowerSearch),
        );

        // Move selected to top if in results
        if (currentWholesalerId) {
            const selectedIdx = filtered.findIndex(
                (w) => w.id === currentWholesalerId,
            );
            if (selectedIdx > 0) {
                const [selected] = filtered.splice(selectedIdx, 1);
                filtered.unshift(selected);
            }
        }

        return filtered;
    }, [wholesalers, searchTerm, currentWholesalerId]);

    // Handle wholesaler selection
    const handleSelect = (wholesaler) => {
        onSelect({
            wholesalerId: wholesaler.id,
            wholesalerName: wholesaler.name,
        });
        onClose();
    };

    // Get wholesaler-specific colors from palette
    const colors = getPickerColors(darkMode, 'wholesaler');
    const modalColors = getModalColors(darkMode);

    // Custom scrollbar styles matching partner picker
    const scrollbarStyles = {
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: darkMode ? '#2D3748' : '#F1F1F1',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: darkMode ? '#4A5568' : '#C1C1C1',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: darkMode ? '#718096' : '#A1A1A1',
        },
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            closeOnOverlayClick={true}>
            <ModalOverlay />
            <ModalContent bg={modalColors.background} maxW="500px">
                <ModalHeader
                    borderBottom={`1px solid ${modalColors.headerBorder}`}
                    py={3}>
                    <HStack justify="space-between" align="center">
                        <Text fontSize="md">{title}</Text>
                        <IconButton
                            aria-label="Close"
                            icon={<CloseIcon boxSize={3} />}
                            size="sm"
                            variant="ghost"
                            onClick={onClose}
                            color="black"
                            _hover={{ bg: darkMode ? '#4A5568' : 'gray.200' }}
                        />
                    </HStack>
                </ModalHeader>
                <ModalBody p={3}>
                    {/* Search Box - at top */}
                    <InputGroup mb={3} size="sm">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="black" boxSize={3} />
                        </InputLeftElement>
                        <Input
                            ref={searchInputRef}
                            placeholder="Search Wholesaler..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            bg={modalColors.searchBackground}
                            borderColor={modalColors.searchBorder}
                            color={darkMode ? 'white' : 'black'}
                            _placeholder={{
                                color: darkMode ? 'gray.400' : 'gray.500',
                            }}
                            _focus={{
                                borderColor: colors.selectedBorder,
                                boxShadow: `0 0 0 1px ${colors.selectedBorder}`,
                            }}
                        />
                        {searchTerm && (
                            <InputRightElement width="auto" pr={2}>
                                <IconButton
                                    aria-label="Clear search"
                                    icon={<CloseIcon boxSize={2} />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => setSearchTerm('')}
                                    color="black"
                                />
                            </InputRightElement>
                        )}
                    </InputGroup>

                    {/* Loading State */}
                    {isLoading && (
                        <Box textAlign="center" py={8}>
                            <Text>Loading...</Text>
                        </Box>
                    )}

                    {/* Error State */}
                    {error && (
                        <Box color="red.500" py={4}>
                            <Text>{error}</Text>
                        </Box>
                    )}

                    {/* Wholesalers List */}
                    {!isLoading && !error && (
                        <VStack
                            align="stretch"
                            spacing={1}
                            maxH="350px"
                            overflowY="auto"
                            pr={1}
                            sx={scrollbarStyles}>
                            {filteredWholesalers.map((wholesaler) => {
                                const isSelected =
                                    wholesaler.id === currentWholesalerId;

                                return (
                                    <Box
                                        key={wholesaler.id}
                                        p={2}
                                        bg={
                                            isSelected
                                                ? colors.selectedBackground
                                                : colors.background
                                        }
                                        borderRadius="md"
                                        cursor="pointer"
                                        _hover={{
                                            bg: isSelected
                                                ? colors.selectedHoverBackground
                                                : colors.hoverBackground,
                                            borderColor: isSelected
                                                ? colors.selectedHoverBorder
                                                : colors.hoverBorder,
                                        }}
                                        onClick={() => handleSelect(wholesaler)}
                                        borderWidth="1px"
                                        borderColor={
                                            isSelected
                                                ? colors.selectedBorder
                                                : colors.border
                                        }>
                                        <Text
                                            fontSize="sm"
                                            color={colors.text}
                                            fontWeight={
                                                isSelected ? 'medium' : 'normal'
                                            }>
                                            {wholesaler.name}
                                        </Text>
                                    </Box>
                                );
                            })}

                            {filteredWholesalers.length === 0 && (
                                <Box textAlign="center" py={6}>
                                    <Text color="gray.500" fontSize="sm">
                                        No wholesalers found
                                    </Text>
                                </Box>
                            )}
                        </VStack>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default WholesalerPicker;
